import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import TecladoNumerico from './TecladoNumerico';

const COR = {
  laranja:     '#F47C3C',
  laranjaEsc:  '#D9651F',
  laranjaClr:  '#FDEFE6',
  verde:       '#1B6B2F',
  verdeEsc:    '#0F4A1F',
  verdeClr:    '#E8F2EB',
  preto:       '#1A1A1A',
  cinza1:      '#4A5563',
  cinza2:      '#8A95A4',
  cinza3:      '#D5DAE2',
  cinza4:      '#F4F6F9',
  branco:      '#FFFFFF',
  erro:        '#D14848',
  erroBg:      '#FDEAEA',
};

// Injeta a fonte Outfit do Google Fonts no head do documento
const carregarFonte = () => {
  if (document.getElementById('rg-font')) return;
  const link = document.createElement('link');
  link.id = 'rg-font';
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap';
  document.head.appendChild(link);
};


// Componente que mostra os requisitos da senha com checkmarks dinamicos
function RequisitosSenha({ senha }) {
  const reqs = [
    { ok: senha.length >= 8,                      txt: 'Pelo menos 8 caracteres' },
    { ok: /[A-Z]/.test(senha),                    txt: 'Uma letra maiúscula' },
    { ok: /[a-z]/.test(senha),                    txt: 'Uma letra minúscula' },
    { ok: /[0-9]/.test(senha),                    txt: 'Um número' },
    { ok: /[!@#$%^&*(),.?":{}|<>]/.test(senha),   txt: 'Um símbolo (!@#$%...)' },
  ];
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '6px 12px',
      fontSize: '12px',
      lineHeight: 1.4,
    }}>
      {reqs.map((r, i) => (
        <div key={i} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: r.ok ? '#1B6B2F' : '#8A95A4',
          fontWeight: r.ok ? 600 : 400,
          transition: 'color 0.2s',
        }}>
          <span style={{
            width: '14px', height: '14px',
            borderRadius: '50%',
            background: r.ok ? '#1B6B2F' : '#E1E5EB',
            color: '#FFF',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '10px', fontWeight: 700,
            transition: 'background 0.2s',
            flexShrink: 0,
          }}>
            {r.ok ? '✓' : ''}
          </span>
          {r.txt}
        </div>
      ))}
    </div>
  );
}

// Campo de código OTP · 6 casas segmentadas (padrão universal de login).
// Avança sozinho ao digitar, backspace volta, aceita colar, trava em 6 dígitos.
function CodigoOTP({ valor, onChange, onComplete, autoFocus }) {
  const LEN = 6;
  const refs = useRef([]);
  const [foco, setFoco] = useState(-1);
  const val = (valor || '').replace(/\D/g, '').slice(0, LEN);

  useEffect(() => {
    if (autoFocus && refs.current[0]) refs.current[0].focus();
  }, [autoFocus]);

  const focar = (i) => {
    const j = Math.max(0, Math.min(i, LEN - 1));
    if (refs.current[j]) refs.current[j].focus();
  };

  const aoDigitar = (i, e) => {
    const digs = e.target.value.replace(/\D/g, '');
    if (!digs) return;
    const arr = val.split('');
    for (let k = 0; k < digs.length && i + k < LEN; k++) arr[i + k] = digs[k];
    const novo = arr.join('').slice(0, LEN);
    onChange(novo);
    focar(i + digs.length);
    if (novo.length === LEN && onComplete) onComplete(novo);
  };

  const aoTeclar = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      const arr = val.split('');
      if (arr[i]) { arr[i] = ''; }
      else if (i > 0) { arr[i - 1] = ''; focar(i - 1); }
      onChange(arr.join('').slice(0, LEN));
    } else if (e.key === 'ArrowLeft') { e.preventDefault(); focar(i - 1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); focar(i + 1); }
  };

  const aoColar = (e) => {
    e.preventDefault();
    const digs = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, LEN);
    if (!digs) return;
    onChange(digs);
    focar(digs.length);
    if (digs.length === LEN && onComplete) onComplete(digs);
  };

  return (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
      {Array.from({ length: LEN }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={val[i] || ''}
          onChange={(e) => aoDigitar(i, e)}
          onKeyDown={(e) => aoTeclar(i, e)}
          onPaste={aoColar}
          onFocus={() => setFoco(i)}
          onBlur={() => setFoco(-1)}
          style={{
            width: '100%', height: '54px', textAlign: 'center',
            fontSize: '22px', fontWeight: 700, color: COR.preto,
            background: COR.branco,
            border: `1.5px solid ${foco === i ? COR.verde : COR.cinza3}`,
            borderRadius: 0, outline: 'none',
            boxShadow: foco === i ? `0 0 0 4px ${COR.verdeClr}` : 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
            fontFamily: "'Outfit', sans-serif",
          }}
        />
      ))}
    </div>
  );
}

export default function Auth({ recoveryMode = false, onRecoveryModeChange }) {
  const [modo, setModo] = useState(recoveryMode ? 'redefinir' : 'login');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [nome, setNome] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState(null);
  const [focado, setFocado] = useState(null);
  const [senhaConf, setSenhaConf] = useState('');
  const [verSenha, setVerSenha] = useState(false);
  const [verSenhaConf, setVerSenhaConf] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [tecladoCodigo, setTecladoCodigo] = useState(false);  // teclado numérico flutuante do campo OTP

  useEffect(() => {
    carregarFonte();
    // Fallback: se chegou via link de recuperacao (hash #type=recovery) e App.js
    // ainda nao detectou via onAuthStateChange, garante o modo redefinir.
    if (!recoveryMode && window.location.hash.includes('type=recovery')) {
      setModo('redefinir');
    }
  }, [recoveryMode]);

  const validarSenha = (s) => {
    if (s.length < 8) return 'A senha precisa ter pelo menos 8 caracteres.';
    if (!/[A-Z]/.test(s)) return 'Inclua pelo menos uma letra maiúscula.';
    if (!/[a-z]/.test(s)) return 'Inclua pelo menos uma letra minúscula.';
    if (!/[0-9]/.test(s)) return 'Inclua pelo menos um número.';
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(s)) return 'Inclua pelo menos um símbolo (!@#$%...).';
    return null;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErro(null); setCarregando(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password: senha });
      if (error) throw error;
    } catch (e) {
      if (e.message.includes('Invalid login credentials')) setErro('E-mail ou senha incorretos.');
      else if (e.message.includes('Email not confirmed')) setErro('Confirme seu e-mail antes de entrar. Verifique sua caixa de entrada.');
      else setErro(e.message);
    }
    setCarregando(false);
  };

  const handleCadastro = async (e) => {
    e.preventDefault();
    setErro(null);
    if (!nome.trim()) { setErro('Informe seu nome.'); return; }
    const eS = validarSenha(senha);
    if (eS) { setErro(eS); return; }
    if (senha !== senhaConf) { setErro('As senhas não conferem. Confirme corretamente.'); return; }
    setCarregando(true);
    try {
      const { error } = await supabase.auth.signUp({ email, password: senha, options: { data: { nome } } });
      if (error) throw error;
      setCodigo('');
      setTecladoCodigo(false);
      setModo('codigo-signup');
    } catch (e) {
      if (e.message.includes('already registered')) setErro('Este e-mail já está cadastrado. Faça login.');
      else setErro(e.message);
    }
    setCarregando(false);
  };

  const handleEsqueci = async (e) => {
    e.preventDefault();
    setErro(null); setCarregando(true);
    try {
      // OTP por código: o e-mail de recuperação leva um código de verificação
      // (template do Supabase usa {{ .Token }}). Imune a scanners/prefetch de link.
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
      setCodigo('');
      setTecladoCodigo(false);
      // Mantém a tela de recuperação montada mesmo após o verifyOtp criar sessão,
      // para não pular a etapa de nova senha (App zera isso no USER_UPDATED).
      if (onRecoveryModeChange) onRecoveryModeChange(true);
      setModo('codigo-recovery');
    } catch (e) {
      setErro(e.message);
    }
    setCarregando(false);
  };

  const handleRedefinir = async (e) => {
    e.preventDefault();
    setErro(null);
    const eS = validarSenha(senha);
    if (eS) { setErro(eS); return; }
    if (senha !== senhaConf) { setErro('As senhas não conferem. Confirme corretamente.'); return; }
    setCarregando(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: senha });
      if (error) throw error;
      // Limpa o hash de recuperacao da URL para evitar reentrar no modo redefinir num refresh
      window.history.replaceState(null, '', window.location.pathname);
      // Apos updateUser, a sessao ja esta ativa — App.js roteia para a tela logada
    } catch (e) {
      setErro(e.message);
    }
    setCarregando(false);
  };

  // Recuperação · ETAPA 1: valida SÓ o código (verifyOtp). Só avança para a
  // etapa de nova senha se o código for válido. Padrão universal de OTP.
  const handleVerificarRecovery = async (e, codigoArg) => {
    if (e && e.preventDefault) e.preventDefault();
    if (carregando) return;
    const code = codigoArg != null ? codigoArg : codigo;
    setErro(null);
    if (code.length !== 6) { setErro('Digite os 6 dígitos do código enviado ao seu e-mail.'); return; }
    setCarregando(true);
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'recovery' });
      if (error) throw error;
      // Código válido → sessão de recuperação criada. Avança para definir a senha.
      setErro(null);
      setModo('nova-senha-recovery');
    } catch (err) {
      const m = (err.message || '').toLowerCase();
      if (m.includes('token') || m.includes('expired') || m.includes('invalid') || m.includes('otp'))
        setErro('Código inválido ou expirado. Verifique e tente novamente.');
      else setErro(err.message);
    }
    setCarregando(false);
  };

  // Recuperação · ETAPA 2: define a nova senha (a sessão já existe do verifyOtp).
  const handleDefinirNovaSenha = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (carregando) return;
    setErro(null);
    const eS = validarSenha(senha);
    if (eS) { setErro(eS); return; }
    if (senha !== senhaConf) { setErro('As senhas não conferem. Confirme corretamente.'); return; }
    setCarregando(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: senha });
      if (error) throw error;
      window.history.replaceState(null, '', window.location.pathname);
      // updateUser dispara USER_UPDATED → App zera recuperandoSenha → roteia para o app.
    } catch (err) {
      setErro(err.message);
    }
    setCarregando(false);
  };

  // Cadastro via código (OTP) · confirma a conta com o código do e-mail.
  const handleVerificarSignup = async (e, codigoArg) => {
    if (e && e.preventDefault) e.preventDefault();
    if (carregando) return;
    const code = codigoArg != null ? codigoArg : codigo;
    setErro(null);
    if (code.length !== 6) { setErro('Digite os 6 dígitos do código enviado ao seu e-mail.'); return; }
    setCarregando(true);
    try {
      const { error } = await supabase.auth.verifyOtp({ email, token: code, type: 'signup' });
      if (error) throw error;
      // Sessão criada — App.js roteia para a tela logada (primeiro acesso → criar empresa)
    } catch (err) {
      const m = (err.message || '').toLowerCase();
      if (m.includes('token') || m.includes('expired') || m.includes('invalid')) setErro('Código inválido ou expirado. Peça um novo.');
      else setErro(err.message);
    }
    setCarregando(false);
  };

  // ==================== ESTILOS ====================
  const fonte = "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif";

  const Page = {
    minHeight: '100vh',
    display: 'flex',
    fontFamily: fonte,
    background: COR.branco,
    color: COR.preto,
  };

  const PanelEsq = {
    flex: '0 0 44%',
    background: COR.laranja,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '56px 56px',
    color: COR.branco,
    overflow: 'hidden',
  };

  const PanelDir = {
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
  };

  const Form = {
    width: '100%',
    maxWidth: '400px',
    animation: 'fadeUp 0.6s ease-out',
  };

  const Label = (campo) => ({
    display: 'block',
    fontSize: '13px',
    fontWeight: 500,
    color: focado === campo ? COR.verde : COR.cinza1,
    marginBottom: '8px',
    letterSpacing: '0.01em',
    transition: 'color 0.2s',
  });

  const Input = (campo) => ({
    width: '100%',
    padding: '14px 16px',
    fontSize: '15px',
    fontFamily: fonte,
    fontWeight: 400,
    color: COR.preto,
    background: COR.branco,
    border: `1.5px solid ${focado === campo ? COR.verde : COR.cinza3}`,
    borderRadius: 0,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    boxShadow: focado === campo ? `0 0 0 4px ${COR.verdeClr}` : 'none',
  });

  const Botao = {
    width: '100%',
    padding: '15px',
    fontSize: '15px',
    fontWeight: 600,
    fontFamily: fonte,
    letterSpacing: '0.01em',
    color: COR.branco,
    background: carregando ? COR.cinza2 : COR.laranja,
    border: 'none',
    borderRadius: 0,
    cursor: carregando ? 'not-allowed' : 'pointer',
    marginTop: '8px',
    transition: 'background 0.2s, transform 0.1s',
    boxShadow: '0 1px 2px rgba(244, 124, 60, 0.3)',
  };

  const LinkBtn = {
    background: 'none',
    border: 'none',
    color: COR.verde,
    fontWeight: 600,
    fontFamily: fonte,
    cursor: 'pointer',
    fontSize: '14px',
    padding: 0,
    textDecoration: 'underline',
    textDecorationColor: COR.verdeClr,
    textUnderlineOffset: '3px',
  };

  const Titulo = {
    fontSize: '28px',
    fontWeight: 700,
    color: COR.preto,
    marginBottom: '8px',
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  };

  const Subtitulo = {
    fontSize: '15px',
    color: COR.cinza1,
    marginBottom: '32px',
    lineHeight: 1.5,
  };

  const Erro = {
    color: COR.erro,
    background: COR.erroBg,
    padding: '12px 14px',
    borderRadius: 0,
    fontSize: '13.5px',
    marginTop: '14px',
    fontWeight: 500,
    lineHeight: 1.4,
  };

  // ============== ICONES + ESTILOS DO OLHO ==============
  const olhinhoBtn = {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '6px',
    color: COR.cinza2,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0,
    transition: 'color 0.2s, background 0.2s',
  };

  const IconeOlho = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );

  const IconeOlhoFechado = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );

  // Teclado numérico on-screen do campo OTP (regra pétria: todo campo numérico
  // tem o <TecladoNumerico/> do app). FLUTUANTE, sem backdrop, ancorado sobre o
  // DIVISOR das duas colunas (44%), na faixa vazia entre o painel de marca e o
  // formulário — não cobre as casas nem o resto. Abre ao clicar nas casas;
  // teclado físico e colar funcionam por cima; limitado a 6. No mobile (≤880px)
  // a classe rg-teclado-otp o esconde (entra o teclado nativo do aparelho).
  const tecladoCodigoEl = tecladoCodigo ? (
    <div className="rg-teclado-otp" style={{
      position: 'fixed', left: '59%', top: '46%',
      transform: 'translate(-50%, -50%)', zIndex: 9999,
      width: '190px', borderRadius: 0,
      boxShadow: '0 12px 40px rgba(0,0,0,0.22)',
    }}>
      <TecladoNumerico
        valor={codigo}
        label="Código"
        permitirDecimal={false}
        onDigito={(d) => setCodigo(v => (v + d).replace(/\D/g, '').slice(0, 6))}
        onApagar={() => setCodigo(v => v.slice(0, -1))}
        onLimpar={() => setCodigo('')}
        onFechar={() => setTecladoCodigo(false)}
      />
    </div>
  ) : null;

  // ============== RENDER ==============
  return (
    <>
      {/* CSS injetado: animações + responsivo */}
      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .rg-btn-primary:hover:not(:disabled) {
          background: ${COR.laranjaEsc} !important;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(244, 124, 60, 0.35) !important;
        }
        .rg-btn-primary:active:not(:disabled) {
          transform: translateY(0);
        }
        button[aria-label*="senha"]:hover {
          color: ${COR.verde} !important;
          background: ${COR.verdeClr} !important;
        }
        .rg-decoracao {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
        }
        @media (max-width: 880px) {
          .rg-painel-esq { display: none !important; }
          .rg-painel-dir { padding: 24px !important; }
          .rg-mobile-logo { display: flex !important; }
        }
        /* Abaixo de 1180px a faixa vazia entre o painel de marca e o formulário
           fica estreita demais para o teclado sem cobrir algo: esconde-o. No
           mobile entra o teclado numérico nativo (inputMode nas casas); no
           desktop estreito, a digitação física. */
        @media (max-width: 1180px) {
          .rg-teclado-otp { display: none !important; }
        }
        /* Nos modos de código, com o teclado à mostra (>1180px), empurra o
           formulário à direita para alargar a faixa vazia e o teclado caber com
           folga. No mobile a classe não desloca (form fica centrado). */
        .rg-form-desloca { transition: transform 0.25s ease; }
        @media (min-width: 1181px) {
          .rg-form-desloca { transform: translateX(84px); }
        }
        @media (min-width: 881px) {
          .rg-mobile-logo { display: none !important; }
        }
      `}</style>

      <div style={Page}>
        {/* PAINEL ESQUERDO — marca */}
        <div className="rg-painel-esq" style={PanelEsq}>
          {/* Círculos decorativos sutis */}
          <div className="rg-decoracao" style={{
            width: '380px', height: '380px',
            background: 'rgba(255,255,255,0.06)',
            top: '-120px', right: '-100px',
          }} />
          <div className="rg-decoracao" style={{
            width: '220px', height: '220px',
            background: 'rgba(255,255,255,0.05)',
            bottom: '-60px', left: '-40px',
          }} />

          {/* Logo (a JPG tem fundo laranja igual ao painel - se funde) */}
          <div style={{ position: 'relative', zIndex: 2 }}>
            <img
              src="/logos/RUNGOAL_LOGO_SF_V2.png"
              alt="RunGoal"
              style={{ width: '180px', height: 'auto', display: 'block' }}
            />
          </div>

          {/* Mensagem da marca */}
          <div style={{ position: 'relative', zIndex: 2, maxWidth: '440px' }}>
            <div style={{
              fontSize: '13px',
              fontWeight: 600,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              opacity: 0.85,
              marginBottom: '20px',
            }}>
              Gestão financeira para empresas
            </div>
            <h1 style={{
              fontSize: '38px',
              fontWeight: 700,
              lineHeight: 1.15,
              letterSpacing: '-0.025em',
              margin: 0,
            }}>
              Clareza para decidir.<br/>
              Tempo para crescer.
            </h1>
            <p style={{
              fontSize: '16px',
              lineHeight: 1.6,
              marginTop: '20px',
              opacity: 0.92,
              fontWeight: 400,
            }}>
              Transforme suas movimentações financeiras em indicadores para conduzir sua empresa com segurança.
            </p>
          </div>

          {/* Rodapé do painel */}
          <div style={{
            position: 'relative', zIndex: 2,
            fontSize: '12.5px', opacity: 0.75, fontWeight: 400,
          }}>
            © {new Date().getFullYear()} RunGoal · Dados protegidos por criptografia
          </div>
        </div>

        {/* PAINEL DIREITO — formulário */}
        <div className="rg-painel-dir" style={PanelDir}>
          <div style={Form}
            className={(modo === 'codigo-signup' || modo === 'codigo-recovery') ? 'rg-form-desloca' : undefined}>
            {/* Logo no mobile (some no desktop) */}
            <div className="rg-mobile-logo" style={{
              justifyContent: 'center', marginBottom: '32px',
            }}>
              <img
                src="/logos/RUNGOAL_LOGO_BRANCO.jpg"
                alt="RunGoal"
                style={{ width: '200px', height: 'auto' }}
              />
            </div>

            {/* CONFIRMAÇÃO DE CADASTRO — por código (OTP) */}
            {modo === 'codigo-signup' && (
              <form onSubmit={handleVerificarSignup}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: 0,
                  background: COR.verdeClr, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px', fontSize: '28px',
                }}>📧</div>
                <h2 style={Titulo}>Confirme seu e-mail</h2>
                <p style={Subtitulo}>
                  Enviamos um <strong style={{ color: COR.preto, fontWeight: 600 }}>código de verificação</strong> para<br/>
                  <strong style={{ color: COR.preto, fontWeight: 600 }}>{email}</strong>.
                  Digite abaixo para ativar sua conta.
                </p>

                <div style={{ marginBottom: '18px' }} onClick={() => setTecladoCodigo(true)}>
                  <label style={{ ...Label('codigo'), color: COR.cinza1 }}>Código de verificação</label>
                  <CodigoOTP valor={codigo} onChange={setCodigo}
                    onComplete={(code) => handleVerificarSignup(null, code)} autoFocus />
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Confirmando...' : 'Confirmar conta'}
                </button>

                <div style={{ textAlign: 'center', marginTop: '28px', fontSize: '14px', color: COR.cinza1 }}>
                  Não recebeu?{' '}
                  <button type="button"
                    onClick={() => { setModo('cadastro'); setErro(null); setCodigo(''); }}
                    style={LinkBtn}>
                    Voltar
                  </button>
                </div>
              </form>
            )}

            {/* LOGIN */}
            {modo === 'login' && (
              <form onSubmit={handleLogin}>
                <h2 style={Titulo}>Bem-vindo de volta.</h2>
                <p style={Subtitulo}>Entre com seu e-mail e senha para continuar.</p>

                <div style={{ marginBottom: '18px' }}>
                  <label style={Label('email')}>E-mail</label>
                  <input
                    type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocado('email')}
                    onBlur={() => setFocado(null)}
                    required style={Input('email')}
                    placeholder="seu@email.com"
                  />
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={Label('senha')}>Senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={verSenha ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      onFocus={() => setFocado('senha')}
                      onBlur={() => setFocado(null)}
                      required
                      style={{ ...Input('senha'), paddingRight: '44px' }}
                      placeholder="••••••••"
                    />
                    <button type="button" onClick={() => setVerSenha(!verSenha)}
                      aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      style={olhinhoBtn}>
                      {verSenha ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                  <div style={{ textAlign: 'right', marginTop: '10px' }}>
                    <button type="button"
                      onClick={() => { setModo('esqueci'); setErro(null); setCodigo(''); }}
                      style={{ ...LinkBtn, fontSize: '13px' }}>
                      Esqueci minha senha
                    </button>
                  </div>
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Entrando...' : 'Entrar'}
                </button>

                <div style={{
                  textAlign: 'center', marginTop: '28px',
                  fontSize: '14px', color: COR.cinza1,
                }}>
                  Ainda não tem conta?{' '}
                  <button type="button"
                    onClick={() => { setModo('cadastro'); setErro(null); }}
                    style={LinkBtn}>
                    Cadastre-se
                  </button>
                </div>
              </form>
            )}

            {/* CADASTRO */}
            {modo === 'cadastro' && (
              <form onSubmit={handleCadastro}>
                <h2 style={Titulo}>Criar conta</h2>
                <p style={Subtitulo}>Leva menos de um minuto para começar.</p>

                <div style={{ marginBottom: '18px' }}>
                  <label style={Label('nome')}>Nome</label>
                  <input type="text" value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    onFocus={() => setFocado('nome')}
                    onBlur={() => setFocado(null)}
                    required style={Input('nome')}
                    placeholder="Seu nome" />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={Label('email')}>E-mail</label>
                  <input type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocado('email')}
                    onBlur={() => setFocado(null)}
                    required style={Input('email')}
                    placeholder="seu@email.com" />
                </div>

                <div style={{ marginBottom: '18px' }}>
                  <label style={Label('senha')}>Senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={verSenha ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      onFocus={() => setFocado('senha')}
                      onBlur={() => setFocado(null)}
                      required
                      style={{ ...Input('senha'), paddingRight: '44px' }}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button type="button" onClick={() => setVerSenha(!verSenha)}
                      aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      style={olhinhoBtn}>
                      {verSenha ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <RequisitosSenha senha={senha} />
                  </div>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={Label('senhaConf')}>Confirmar senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={verSenhaConf ? 'text' : 'password'}
                      value={senhaConf}
                      onChange={(e) => setSenhaConf(e.target.value)}
                      onFocus={() => setFocado('senhaConf')}
                      onBlur={() => setFocado(null)}
                      required
                      style={{ ...Input('senhaConf'), paddingRight: '44px' }}
                      placeholder="Repita a senha"
                    />
                    <button type="button" onClick={() => setVerSenhaConf(!verSenhaConf)}
                      aria-label={verSenhaConf ? 'Ocultar senha' : 'Mostrar senha'}
                      style={olhinhoBtn}>
                      {verSenhaConf ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Criando conta...' : 'Criar conta'}
                </button>

                <div style={{
                  textAlign: 'center', marginTop: '28px',
                  fontSize: '14px', color: COR.cinza1,
                }}>
                  Já tem conta?{' '}
                  <button type="button"
                    onClick={() => { setModo('login'); setErro(null); setSenhaConf(''); }}
                    style={LinkBtn}>
                    Entrar
                  </button>
                </div>
              </form>
            )}

            {/* ESQUECI MINHA SENHA — pedido do código */}
            {modo === 'esqueci' && (
              <form onSubmit={handleEsqueci}>
                <h2 style={Titulo}>Recuperar senha</h2>
                <p style={Subtitulo}>Informe seu e-mail. Vamos enviar um código de verificação para você redefinir sua senha.</p>

                <div style={{ marginBottom: '8px' }}>
                  <label style={Label('email')}>E-mail</label>
                  <input type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocado('email')}
                    onBlur={() => setFocado(null)}
                    required style={Input('email')}
                    placeholder="seu@email.com" />
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Enviando...' : 'Enviar código'}
                </button>

                <div style={{
                  textAlign: 'center', marginTop: '28px',
                  fontSize: '14px', color: COR.cinza1,
                }}>
                  Lembrou a senha?{' '}
                  <button type="button"
                    onClick={() => { setModo('login'); setErro(null); }}
                    style={LinkBtn}>
                    Voltar ao login
                  </button>
                </div>
              </form>
            )}

            {/* RECUPERAÇÃO · ETAPA 1 — validar o código (OTP) */}
            {modo === 'codigo-recovery' && (
              <form onSubmit={handleVerificarRecovery}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: 0,
                  background: COR.verdeClr, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px', fontSize: '28px',
                }}>📧</div>
                <h2 style={Titulo}>Redefinir senha</h2>
                <p style={Subtitulo}>
                  Enviamos um <strong style={{ color: COR.preto, fontWeight: 600 }}>código de verificação</strong> para{' '}
                  <strong style={{ color: COR.preto, fontWeight: 600 }}>{email}</strong>. Digite-o para continuar.
                </p>

                <div style={{ marginBottom: '18px' }} onClick={() => setTecladoCodigo(true)}>
                  <label style={{ ...Label('codigo'), color: COR.cinza1 }}>Código de verificação</label>
                  <CodigoOTP valor={codigo} onChange={setCodigo}
                    onComplete={(code) => handleVerificarRecovery(null, code)} autoFocus />
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Verificando...' : 'Verificar código'}
                </button>

                <div style={{ textAlign: 'center', marginTop: '28px', fontSize: '14px', color: COR.cinza1 }}>
                  Lembrou a senha?{' '}
                  <button type="button"
                    onClick={() => { setModo('login'); setErro(null); setCodigo(''); if (onRecoveryModeChange) onRecoveryModeChange(false); }}
                    style={LinkBtn}>
                    Voltar ao login
                  </button>
                </div>
              </form>
            )}

            {/* RECUPERAÇÃO · ETAPA 2 — definir a nova senha (código já validado) */}
            {modo === 'nova-senha-recovery' && (
              <form onSubmit={handleDefinirNovaSenha}>
                <div style={{
                  width: '56px', height: '56px', borderRadius: 0,
                  background: COR.verdeClr, display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  marginBottom: '24px', fontSize: '30px',
                  color: COR.verde, fontWeight: 800,
                }}>✓</div>
                <h2 style={Titulo}>Criar nova senha</h2>
                <p style={Subtitulo}>Código verificado. Escolha uma senha forte para acessar sua conta.</p>

                <div style={{ marginBottom: '18px' }}>
                  <label style={Label('senha')}>Nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input type={verSenha ? 'text' : 'password'} value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      onFocus={() => setFocado('senha')} onBlur={() => setFocado(null)}
                      required style={{ ...Input('senha'), paddingRight: '44px' }}
                      placeholder="Mínimo 8 caracteres" />
                    <button type="button" onClick={() => setVerSenha(!verSenha)}
                      aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'} style={olhinhoBtn}>
                      {verSenha ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <RequisitosSenha senha={senha} />
                  </div>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={Label('senhaConf')}>Confirmar nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input type={verSenhaConf ? 'text' : 'password'} value={senhaConf}
                      onChange={(e) => setSenhaConf(e.target.value)}
                      onFocus={() => setFocado('senhaConf')} onBlur={() => setFocado(null)}
                      required style={{ ...Input('senhaConf'), paddingRight: '44px' }}
                      placeholder="Repita a senha" />
                    <button type="button" onClick={() => setVerSenhaConf(!verSenhaConf)}
                      aria-label={verSenhaConf ? 'Ocultar senha' : 'Mostrar senha'} style={olhinhoBtn}>
                      {verSenhaConf ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Salvando...' : 'Redefinir senha'}
                </button>

                <div style={{ textAlign: 'center', marginTop: '28px', fontSize: '14px', color: COR.cinza1 }}>
                  <button type="button"
                    onClick={async () => { await supabase.auth.signOut(); if (onRecoveryModeChange) onRecoveryModeChange(false); setModo('login'); setCodigo(''); setSenha(''); setSenhaConf(''); setErro(null); }}
                    style={LinkBtn}>
                    Cancelar
                  </button>
                </div>
              </form>
            )}

            {/* REDEFINIR SENHA (apos clicar no link do email) */}
            {modo === 'redefinir' && (
              <form onSubmit={handleRedefinir}>
                <h2 style={Titulo}>Definir nova senha</h2>
                <p style={Subtitulo}>Escolha uma senha forte. Após confirmar, você entrará automaticamente.</p>

                <div style={{ marginBottom: '18px' }}>
                  <label style={Label('senha')}>Nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={verSenha ? 'text' : 'password'}
                      value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      onFocus={() => setFocado('senha')}
                      onBlur={() => setFocado(null)}
                      required
                      style={{ ...Input('senha'), paddingRight: '44px' }}
                      placeholder="Mínimo 8 caracteres"
                    />
                    <button type="button" onClick={() => setVerSenha(!verSenha)}
                      aria-label={verSenha ? 'Ocultar senha' : 'Mostrar senha'}
                      style={olhinhoBtn}>
                      {verSenha ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    <RequisitosSenha senha={senha} />
                  </div>
                </div>

                <div style={{ marginBottom: '8px' }}>
                  <label style={Label('senhaConf')}>Confirmar nova senha</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={verSenhaConf ? 'text' : 'password'}
                      value={senhaConf}
                      onChange={(e) => setSenhaConf(e.target.value)}
                      onFocus={() => setFocado('senhaConf')}
                      onBlur={() => setFocado(null)}
                      required
                      style={{ ...Input('senhaConf'), paddingRight: '44px' }}
                      placeholder="Repita a senha"
                    />
                    <button type="button" onClick={() => setVerSenhaConf(!verSenhaConf)}
                      aria-label={verSenhaConf ? 'Ocultar senha' : 'Mostrar senha'}
                      style={olhinhoBtn}>
                      {verSenhaConf ? IconeOlhoFechado : IconeOlho}
                    </button>
                  </div>
                </div>

                {erro && <div style={Erro}>{erro}</div>}

                <button type="submit" disabled={carregando}
                  className="rg-btn-primary" style={Botao}>
                  {carregando ? 'Definindo...' : 'Definir nova senha'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
      {/* Teclado OTP fora do formulário: o form usa transform (desloca), que
          criaria containing block e prenderia o position:fixed a ele. Aqui fica
          relativo à viewport. */}
      {tecladoCodigoEl}
    </>
  );
}